import React from 'react';

interface SkeletonProps {
    variant?: 'text' | 'circle' | 'rectangular' | 'card';
    width?: string | number;
    height?: string | number;
    className?: string;
}

/**
 * Premium Skeleton Loader Component
 * 
 * Uses the shimmer animation from index.css for a modern loading experience.
 * Supports multiple variants to match different content types.
 */
export const Skeleton: React.FC<SkeletonProps> = ({
    variant = 'rectangular',
    width,
    height,
    className = ''
}) => {
    const baseClasses = 'animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:400%_100%]';

    const variantClasses = {
        text: 'h-4 rounded',
        circle: 'rounded-full',
        rectangular: 'rounded-lg',
        card: 'rounded-2xl'
    };

    const style: React.CSSProperties = {
        width: width || (variant === 'circle' ? '40px' : '100%'),
        height: height || (variant === 'text' ? '16px' : variant === 'circle' ? '40px' : '120px')
    };

    return (
        <div
            className={`${baseClasses} ${variantClasses[variant]} ${className}`}
            style={style}
        />
    );
};

/**
 * TableSkeleton - For loading states in data tables
 */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
    rows = 5,
    columns = 6
}) => {
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/80">
                    <tr>
                        {Array.from({ length: columns }).map((_, i) => (
                            <th key={i} className="px-6 py-4">
                                <Skeleton variant="text" width="80px" height="12px" />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <tr key={rowIndex} className="hover:bg-gray-50/50">
                            {Array.from({ length: columns }).map((_, colIndex) => (
                                <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
                                    {colIndex === 0 ? (
                                        // First column: Avatar + Name
                                        <div className="flex items-center gap-4">
                                            <Skeleton variant="circle" width="40px" height="40px" />
                                            <div className="space-y-2">
                                                <Skeleton variant="text" width="120px" />
                                                <Skeleton variant="text" width="80px" height="12px" />
                                            </div>
                                        </div>
                                    ) : (
                                        <Skeleton variant="text" width={`${Math.random() * 40 + 60}px`} />
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * CardSkeleton - For dashboard stats and content cards
 */
export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => {
    return (
        <>
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <Skeleton variant="text" width="100px" height="14px" />
                        <Skeleton variant="circle" width="48px" height="48px" />
                    </div>
                    <Skeleton variant="text" width="140px" height="32px" className="mb-2" />
                    <Skeleton variant="text" width="80px" height="12px" />
                </div>
            ))}
        </>
    );
};

/**
 * ListSkeleton - For mobile card-style lists
 */
export const ListSkeleton: React.FC<{ items?: number }> = ({ items = 3 }) => {
    return (
        <div className="space-y-4">
            {Array.from({ length: items }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <div className="flex items-start gap-4">
                        <Skeleton variant="circle" width="48px" height="48px" />
                        <div className="flex-1 space-y-3">
                            <Skeleton variant="text" width="70%" height="16px" />
                            <Skeleton variant="text" width="50%" height="14px" />
                            <div className="flex gap-2 pt-2">
                                <Skeleton variant="rectangular" width="60px" height="24px" />
                                <Skeleton variant="rectangular" width="60px" height="24px" />
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Skeleton;
